import type { SimulationQuote } from '~/store/billing'
import type { AnalyticsItem } from '~/composables/useAnalytics'
import { microCentsToMajor } from '~/composables/useAnalytics'

/**
 * How a quote is described to GA4 and Microsoft Ads.
 *
 * Pure functions, shared by BOTH checkout surfaces. The public configurator and
 * the signed-in checkout are now two pages reporting into one funnel, and if
 * they shaped their items differently the same server would appear as two
 * different products depending on where the customer started — which is exactly
 * the kind of drift that makes a conversion report untrustworthy without
 * anybody being able to say why.
 */
export function quoteToAnalyticsItems(quote: SimulationQuote | null): AnalyticsItem[] {
  if (!quote) return []
  return quote.line_items
    .filter((li) => !li.rejected)
    .map((li) => {
      const qty = Math.max(li.quantity ?? 1, 1)
      const subtotal = li.cycle_subtotal_micro_cents ?? li.hourly_subtotal_micro_cents
      return {
        item_id: li.kind === 'compute' ? quote.offering_code : li.kind,
        item_name: li.label,
        price: Number((microCentsToMajor(subtotal) / qty).toFixed(2)),
        quantity: qty,
      }
    })
}

/**
 * The value of a quote in major currency units.
 *
 * The cycle total where there is one, falling back to the hourly rate for
 * on-demand — the same order the summary displays them in, so a conversion's
 * value matches the figure the customer was looking at.
 */
export function quoteAnalyticsValue(quote: SimulationQuote | null): number {
  return microCentsToMajor(
    quote?.total_cycle_micro_cents ?? quote?.total_hourly_rate_micro_cents,
  )
}
